import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  signOut,
  User,
  sendPasswordResetEmail,
  updateProfile,
  UserCredential,
} from 'firebase/auth';
import { auth } from '@/config/firebase';
import { firestoreService } from './firestoreService';
import { Timestamp } from 'firebase/firestore';

const googleProvider = new GoogleAuthProvider();
const appleProvider = new OAuthProvider('apple.com');

export const authService = {
  // Email/Password Authentication
  async signInWithEmail(email: string, password: string): Promise<UserCredential> {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    // Ensure credits document exists (for existing users who signed up before credits were auto-created)
    await this.createUserDocumentIfNotExists(userCredential.user);
    return userCredential;
  },

  async signUpWithEmail(
    email: string,
    password: string,
    displayName?: string
  ): Promise<UserCredential> {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    if (displayName && userCredential.user) {
      await updateProfile(userCredential.user, { displayName });
    }

    // Create user document in Firestore
    await this.createUserDocument(userCredential.user);
    
    return userCredential;
  },

  // OAuth Authentication
  // Note: COOP warnings in console are harmless - handled by console filter in main.tsx
  async signInWithGoogle(): Promise<UserCredential> {
    const userCredential = await signInWithPopup(auth, googleProvider);
    // Create user document if it doesn't exist (for new users)
    await this.createUserDocumentIfNotExists(userCredential.user);
    return userCredential;
  },

  async signInWithApple(): Promise<UserCredential> {
    const userCredential = await signInWithPopup(auth, appleProvider);
    // Create user document if it doesn't exist (for new users)
    await this.createUserDocumentIfNotExists(userCredential.user);
    return userCredential;
  },

  // Password Reset
  async resetPassword(email: string): Promise<void> {
    return sendPasswordResetEmail(auth, email);
  },

  // Sign Out
  async signOut(): Promise<void> {
    return signOut(auth);
  },

  // Get Current User
  getCurrentUser(): User | null {
    return auth.currentUser;
  },

  // Create user document in Firestore (only credits, no users collection)
  async createUserDocument(user: User): Promise<void> {
    // Only create credits document - no users collection needed
    // User data comes from Firebase Auth, admin status via custom claims
    const creditsDoc = {
      balance: 50, // Free tier gets 50 credits
      tier: 'free' as const,
      dailyAllowance: 50,
      lastReset: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    await firestoreService.createDocument('credits', creditsDoc, user.uid);
  },

  // Create user document if it doesn't exist (for OAuth users)
  async createUserDocumentIfNotExists(user: User): Promise<void> {
    try {
      // Check if credits document exists (no users collection)
      const existingCredits = await firestoreService.getDocument('credits', user.uid);
      if (!existingCredits) {
        await this.createUserDocument(user);
      }
    } catch (error: any) {
      // If it's a permission error, log it but don't fail auth
      if (error?.code === 'permission-denied') {
        console.warn('Permission denied when checking credits document. User may need to refresh permissions.');
        // Try to create anyway - might work if it's just a read permission issue
        try {
          await this.createUserDocument(user);
        } catch (createError) {
          console.error('Failed to create credits document:', createError);
          // Don't throw - auth succeeded, credits can be created later
        }
      } else {
        // For other errors, try to create the document
        try {
          await this.createUserDocument(user);
        } catch (createError) {
          console.error('Failed to create credits document after error:', createError);
          // Don't throw - auth succeeded, credits can be created later
        }
      }
    }
  },
};


